/** Настройки.
 *
 * Здесь только то, что реально что-то меняет. Расписания дайджеста нет: оно
 * живёт в переменных окружения процесса и общее на всех — переключатель, который
 * пишет в поле, никем не читаемое, был бы такой же ложью, как неживое поле ввода.
 */
import { useEffect, useState } from "react";
import { ApiError, api, type AppSettings, type Failure, type SettingsPatch } from "../lib/api.ts";
import { plural } from "../lib/plural.ts";
import { rowButton } from "../lib/rowStyle.ts";
import { Switch } from "../components/Switch.tsx";
import { addToHomeScreen, homeScreenStatus, tap,
         type HomeScreenStatus } from "../lib/telegram.ts";
import { Header } from "../components/Header.tsx";
import { LimitSheet } from "../components/LimitSheet.tsx";
import { SpanSheet } from "../components/SpanSheet.tsx";
import type { Span } from "../lib/api.ts";
import { formatMoney } from "../lib/money.ts";
import { Fallback } from "./Fallback.tsx";
import { hint } from "../components/ui.tsx";

const CURRENCIES = ["USD", "RUB", "VND"];
const HOURS = [8, 12, 18, 21];

//: Дни для выбора снимка. До 28-го: 29–31 есть не в каждом месяце, а
//: «последний день» уже стоит отдельным пунктом и не пропускает ни одного.
const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

/** Ключи те же, что в `app/bot/ui/palettes.py`: список закрытый с обеих сторон,
 *  и произвольное имя дало бы картинки цветом по умолчанию без объяснения. */
//: Год — для налоговой, месяц и неделя — для себя. День в выгрузке смысла не
//: имеет: за день проще посмотреть ленту, чем открывать файл.
const EXPORTS: { key: "week" | "month" | "year"; label: string }[] = [
  { key: "week", label: "Неделя" },
  { key: "month", label: "Месяц" },
  { key: "year", label: "Год" },
];

/** Значок приложения на главный экран телефона (Bot API 8.0).
 *
 *  Строку показываем, только если клиент это умеет И значка ещё нет: кнопка,
 *  которая ничего не сделает, хуже отсутствующей. Значок открывает приложение
 *  через Telegram — с подписью; закладка из браузера так не работает, потому
 *  что подписи в ней нет и сервер ответит 401.
 */
function HomeScreenRow() {
  const [status, setStatus] = useState<HomeScreenStatus>("unsupported");

  useEffect(() => { homeScreenStatus().then(setStatus); }, []);

  if (status === "added" || status === "unsupported") return null;

  return (
    <Group label="Быстрый доступ"
           hint="Значок откроет приложение через Telegram — вход останется прежним.">
      <button type="button" onClick={() => { tap(); addToHomeScreen(); }}
              style={{ display: "flex", alignItems: "center", gap: "var(--s-3)",
                       width: "100%", minHeight: "var(--tap)", padding: "12px 0",
                       background: "none", border: "none", cursor: "pointer",
                       font: "inherit", color: "inherit", textAlign: "left" }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)"
             strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4.5" y="2.8" width="15" height="18.4" rx="2.4" />
          <path d="M12 8v7M8.6 11.4L12 8l3.4 3.4" />
        </svg>
        <span style={{ flexGrow: 1, fontSize: "var(--f-body)" }}>
          Вынести на главный экран
        </span>
      </button>
    </Group>
  );
}

const PALETTES = [
  { key: "ocean", label: "🌊 Океан" },
  { key: "sunset", label: "🌅 Закат" },
  { key: "vitrage", label: "🔷 Витраж" },
];

export function SettingsScreen({ onBack, onOpen }: {
  onBack: () => void;
  onOpen: (page: "rates" | "categories" | "pins" | "bugs") => void;
}) {
  const [data, setData] = useState<AppSettings | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [busy, setBusy] = useState(false);
  const [limitSheet, setLimitSheet] = useState(false);
  const [pickingSpan, setPickingSpan] = useState(false);
  const [trouble, setTrouble] = useState("");

  const load = () => {
    setFailure(null);
    api.settings().then(setData)
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"));
  };

  useEffect(load, []);

  const patch = (body: SettingsPatch) => {
    if (busy) return;
    tap();
    setBusy(true);
    setTrouble("");
    api.patchSettings(body).then(setData)
      .catch(() => setTrouble("Не получилось сохранить. Попробуй ещё раз."))
      .finally(() => setBusy(false));
  };

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  return (
    <div style={{ padding: "var(--screen)" }}>
      <Header title="Настройки" onBack={onBack} />

      {!data ? <Skeleton /> : (
        <>
          {/* Лимит — здесь тоже, а не только на главной: спека требует паритета
              с настройками чата, а искать ядро продукта на другом экране —
              ровно та работа, которую настройки должны снимать. */}
          <Group label="Сколько готов тратить в месяц"
                 hint="На каждой трате видно, сколько ещё можно. Это не запрет.">
            <button type="button" onClick={() => { tap(); setLimitSheet(true); }}
                    style={{ display: "flex", alignItems: "center", width: "100%",
                             padding: "12px 0", background: "none", border: "none",
                             cursor: "pointer", font: "inherit", color: "inherit",
                             minHeight: "var(--tap)" }}>
              <span className="num" style={{ flexGrow: 1, textAlign: "left",
                                             fontSize: "var(--f-t3)", fontWeight: 600 }}>
                {data.monthly_allowance
                  ? formatMoney(data.monthly_allowance, data.base_currency)
                  : "не задан"}
              </span>
              <span style={{ fontSize: "var(--f-caption)", color: "var(--accent)" }}>
                {data.monthly_allowance ? "Изменить" : "Задать"}
              </span>
            </button>
          </Group>

          <Group label="Итоги считаем в"
                 hint="Уже записанные траты не пересчитываются: у каждой свой курс на дату.">
            <Choice options={CURRENCIES} active={data.base_currency} busy={busy}
                    onPick={(c) => patch({ base_currency: c })} />
          </Group>

          <Group label="Обычно трачу в"
                 hint="Валюта по умолчанию, когда во фразе её не назвал.">
            <Choice options={CURRENCIES} active={data.default_currency} busy={busy}
                    onPick={(c) => patch({ default_currency: c })} />
          </Group>

          {/* Палитра красит картинки, которые бот шлёт в чат (§16.12). Поле в
              базе персональное с самого начала, а поменять его можно было
              только в чате. */}
          <Group label="Цвета на картинках бота"
                 hint="Влияет на новые картинки. Уже отправленные не перерисовываются.">
            <Choice options={PALETTES.map((p) => p.label)} busy={busy}
                    active={PALETTES.find((p) => p.key === data.palette)?.label ?? ""}
                    onPick={(label) => patch({
                      palette: PALETTES.find((p) => p.label === label)!.key,
                    })} />
          </Group>

          <Group label="Напоминания">
            <div style={{ display: "flex", alignItems: "center", padding: "12px 0" }}>
              <span id="proactive-label"
                    style={{ flexGrow: 1, fontSize: "var(--f-body)" }}>
                Писать первым
              </span>
              {/* Подпись уже написана рядом — своя `aria-label` произносила бы её
                  дважды. Связываем видимый текст, а не дублируем его. */}
              <Switch on={data.proactive_enabled} busy={busy} labelledBy="proactive-label"
                      onToggle={() => patch({ proactive_enabled: !data.proactive_enabled })} />
            </div>
            {data.proactive_enabled && (
              <>
                <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                              marginTop: "var(--s-2)" }}>
                  В котором часу
                </div>
                <Choice options={HOURS.map((h) => `${h}:00`)}
                        active={`${data.reminder_hour}:00`} busy={busy}
                        onPick={(v) => patch({ reminder_hour: Number(v.split(":")[0]) })} />
                {data.reminder_hour_is_personal && (
                  <button type="button" aria-disabled={busy || undefined}
                          onClick={() => patch({ reset_reminder_hour: true })}
                          style={{ background: "none", border: "none", cursor: "pointer",
                                   font: "inherit", fontSize: "var(--f-caption)",
                                   color: "var(--accent)", padding: "10px 0", minHeight: 38 }}>
                    Вернуть общее время
                  </button>
                )}
              </>
            )}
            <p style={hint}>
              Вечером напишу, только если за день не было ни одной записи. Записал — тишина.
            </p>
          </Group>

          {/* День снимка капитала. Фиксированный день — не каприз: снимок это
              замер в точке месяца, и пока дата плавала (29.08, 01.09, 01.10,
              05.11), прирост мерил место в месяце, а не накопление. */}
          <Group label="День снимка капитала"
                 hint="В этот день напомню снять капитал, если снимка за месяц ещё нет. Один и тот же день нужен, чтобы приросты соседних месяцев были сравнимы.">
            <select value={data.snapshot_day ?? "last"} aria-label="День снимка капитала"
                    disabled={busy}
                    onChange={(e) => patch(e.target.value === "last"
                      ? { reset_snapshot_day: true }
                      : { snapshot_day: Number(e.target.value) })}
                    style={{ width: "100%", minHeight: 40, borderRadius: "var(--r-sm)",
                             border: "none", font: "inherit", fontSize: "var(--f-body-s)",
                             background: "var(--surface-2)", color: "var(--text)",
                             padding: "0 12px" }}>
              <option value="last">Последний день месяца</option>
              {DAYS.map((d) => <option key={d} value={d}>{d}-е число</option>)}
            </select>
          </Group>

          <section style={{ background: "var(--surface)", borderRadius: "var(--r-lg)",
                            padding: "2px var(--s-5)", marginTop: "var(--s-3)" }}>
            <NavRow label="Категории" hint="имя и тип для разбивки 50/30/20"
                    onOpen={() => onOpen("categories")} />
            <NavRow label="Мои привязки" hint="какие места бот запомнил за какими категориями"
                    onOpen={() => onOpen("pins")} />
            <NavRow label="Курсы" hint="биржевые и заданные вручную"
                    onOpen={() => onOpen("rates")} />
            <NavRow label="Сообщить о проблеме" hint="уйдёт вместе с последними записями"
                    onOpen={() => onOpen("bugs")} last />
          </section>

          <HomeScreenRow />

          <Group label="Выгрузка"
                 hint="CSV со всеми полями, включая курс и его дату.">
            <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--s-2)",
                          padding: "4px 0" }}>
              {EXPORTS.map(({ key, label }) => (
                <a key={key} href={api.exportUrl(key)} download
                   style={{ minHeight: 38, padding: "0 14px", borderRadius: "var(--r-pill)",
                            background: "var(--surface-2)", color: "var(--text)",
                            textDecoration: "none", fontSize: "var(--f-caption)",
                            display: "inline-flex", alignItems: "center" }}>
                  {label}
                </a>
              ))}
              <button type="button" onClick={() => { tap(); setPickingSpan(true); }}
                      style={{ minHeight: 38, padding: "0 14px", borderRadius: "var(--r-pill)",
                               background: "var(--surface-2)", color: "var(--text)",
                               border: "none", cursor: "pointer", font: "inherit",
                               fontSize: "var(--f-caption)" }}>
                Свой период
              </button>
            </div>
          </Group>

          {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
          <p style={{ ...hint, marginTop: "var(--s-6)" }}>
            Недельный итог приходит в {plural(1, ["воскресенье", "воскресенья", "воскресений"])
              .replace("1 ", "")} и настраивается на сервере — он общий на всех.
          </p>
        </>
      )}

      {pickingSpan && (
        <SpanSheet current={null} onClose={() => setPickingSpan(false)}
                   onClear={() => setPickingSpan(false)}
                   onPick={(span: Span) => {
                     setPickingSpan(false);
                     // Ссылку собираем и жмём сами: держать <a> с диапазоном,
                     // которого ещё нет, значит держать ссылку в никуда.
                     const a = document.createElement("a");
                     a.href = api.exportUrl("month", span);
                     a.download = "";
                     a.click();
                   }} />
      )}
      {limitSheet && (
        <LimitSheet state={null} onClose={() => setLimitSheet(false)}
                    onDone={() => { setLimitSheet(false); load(); }} />
      )}

    </div>
  );
}

function NavRow({ label, hint: sub, onOpen, last }: {
  label: string; hint: string; onOpen: () => void; last?: boolean;
}) {
  return (
    <button type="button" onClick={() => { tap(); onOpen(); }} style={rowButton(!!last)}>
      <span style={{ flexGrow: 1 }}>
        <span style={{ display: "block", fontSize: "var(--f-body)" }}>{label}</span>
        <span style={{ display: "block", fontSize: "var(--f-caption)",
                       color: "var(--text-muted)", marginTop: 2 }}>{sub}</span>
      </span>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--text-disabled)"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 6l6 6-6 6" />
      </svg>
    </button>
  );
}

function Group({ label, hint: sub, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  // Подпись группы связываем программно, а не только визуально. На экране две
  // группы валют, и в дереве доступности обе читались как «USD, кнопка, нажата»
  // — шесть кнопок с тремя именами и без признака, к чему они относятся.
  return (
    <section aria-label={label}
             style={{ background: "var(--surface)", borderRadius: "var(--r-lg)",
                      padding: "var(--s-5)", marginTop: "var(--s-3)" }}>
      <div aria-hidden="true"
           style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    marginBottom: "var(--s-3)" }}>
        {label}
      </div>
      {children}
      {sub && <p style={hint}>{sub}</p>}
    </section>
  );
}

function Choice({ options, active, busy, onPick }: {
  options: string[]; active: string; busy: boolean; onPick: (v: string) => void;
}) {
  return (
    <div style={{ display: "flex", gap: "var(--s-2)" }}>
      {options.map((o) => {
        const on = o === active;
        return (
          <button key={o} type="button" aria-disabled={busy || undefined} aria-pressed={on}
                  onClick={() => onPick(o)}
                  style={{ flexGrow: 1, minHeight: 40, borderRadius: "var(--r-sm)", border: "none",
                           cursor: busy ? "default" : "pointer", font: "inherit",
                           fontSize: "var(--f-body-s)", fontWeight: on ? 600 : 400,
                           background: on ? "var(--accent)" : "var(--surface-2)",
                           color: on ? "var(--on-accent)" : "var(--text)" }}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

function Skeleton() {
  return (
    <div style={{ background: "var(--surface)", borderRadius: "var(--r-lg)",
                  padding: "var(--s-5)" }} role="status" aria-busy="true" aria-label="Загружаю настройки">
      <div style={{ height: 11, width: "40%", background: "var(--surface-3)", borderRadius: 5 }} />
      <div style={{ height: 40, background: "var(--surface-2)", borderRadius: 10,
                    marginTop: "var(--s-3)" }} />
    </div>
  );
}

